import React, { useMemo, useState } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import { BaseComponentImpl } from "../../types/implementations";
import { Building, PhotoLocation, PhotoModel } from "../../types/protocols";
import { BaseType } from "../../types/Enums";
import { translate } from "../../types/EnumTranslations";
import BlueprintWithMarkers from "../../Components/BlueprintWithMarkers";
import ComponentCard from "../../Components/ComponentCard";
import { parentPhotoMarkers } from "../../Helpers/photoMarkerGroups";
import { componentTypeCountNames, photoLocationSummaryText } from "../../Helpers/photoLocations";
import "./ChildComponentsSection.css";

interface ChildComponentsSectionProps {
    parent: BaseComponentImpl;
    baseType: BaseType;
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    headingStyle: React.CSSProperties;
    /** Child to highlight in the photos. */
    selectedChildKey?: string | null;
    /** The event lets a caller that opens the child's page honour Cmd/Ctrl/middle-click. */
    onChildClick: (childComponent: BaseComponentImpl, event?: React.MouseEvent) => void;
    onChildPhotoClick?: (photo: PhotoModel) => void;
    onPhotoLocationClick?: (photoLocation: PhotoLocation) => void;
}

/**
 * The children of one base type ("Fönster (24 st)"). Children pinned in the
 * parent's own photos are drawn together, one photo per parent photo with all
 * of their markers; the rest stay cards. "Lista" shows every child as a card.
 */
const ChildComponentsSection: React.FC<ChildComponentsSectionProps> = ({
    parent,
    baseType,
    components,
    building,
    buildingKey,
    headingStyle,
    selectedChildKey,
    onChildClick,
    onChildPhotoClick,
    onPhotoLocationClick,
}) => {
    const [mode, setMode] = useState<'photo' | 'list'>('photo');
    const { photos, pinnedKeys } = useMemo(
        () => parentPhotoMarkers(parent.key, components),
        [parent.key, components]
    );
    const hasPhotos = photos.length > 0;
    const showPhotos = hasPhotos && mode === 'photo';
    const cardComponents = showPhotos ? components.filter(c => !pinnedKeys.has(c.key)) : components;
    const byKey = useMemo(() => new Map(components.map(c => [c.key, c])), [components]);

    const names = componentTypeCountNames(baseType, translate(baseType));
    const summary = photoLocationSummaryText(pinnedKeys.size, photos.length, names.singular, names.plural);

    const cards = (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
            {cardComponents.map((childComponent) => (
                <ComponentCard
                    key={childComponent.key}
                    component={childComponent}
                    building={building}
                    buildingKey={buildingKey}
                    disableThumbnails={!onChildPhotoClick}
                    onPhotoClick={onChildPhotoClick}
                    onPhotoLocationClick={onPhotoLocationClick}
                    onNavigate={(_key, event) => onChildClick(childComponent, event)}
                />
            ))}
        </div>
    );

    return (
        <div>
            <div className="child-components-header" style={headingStyle}>
                <h4>{translate(baseType)} ({components.length} st)</h4>
                {hasPhotos && (
                    <ButtonGroup size="sm" className="child-components-mode">
                        <Button
                            variant={mode === 'photo' ? 'primary' : 'outline-primary'}
                            aria-pressed={mode === 'photo'}
                            onClick={() => setMode('photo')}
                        >
                            Foto
                        </Button>
                        <Button
                            variant={mode === 'list' ? 'primary' : 'outline-primary'}
                            aria-pressed={mode === 'list'}
                            onClick={() => setMode('list')}
                        >
                            Lista
                        </Button>
                    </ButtonGroup>
                )}
            </div>

            {showPhotos ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <p className="child-components-summary">{summary}</p>
                    {photos.map(photo => (
                        <BlueprintWithMarkers
                            key={photo.photoKey}
                            blueprint={photo.photoModel}
                            currentBlueprintKey={photo.photoModel.storagePath}
                            building={building}
                            utilities={photo.markerComponents}
                            selectedUtilityKey={selectedChildKey ?? null}
                            onUtilityClick={(key, event) => {
                                const child = byKey.get(key);
                                if (child) onChildClick(child, event);
                            }}
                            hideTitle
                            compactMarkers
                        />
                    ))}
                    {cardComponents.length > 0 && (
                        <div>
                            <h5 className="child-components-unpinned">Saknar position i foto</h5>
                            {cards}
                        </div>
                    )}
                </div>
            ) : cards}
        </div>
    );
};

export default ChildComponentsSection;
